import { ArrowUpRight, Mail, MapPin, MessageCircle } from "lucide-react";
import { CONTACT, useLang } from "@/i18n";
import Logo from "./Logo";
import { scrollToId } from "./Nav";
import Reveal from "./Reveal";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  const links = [
    { id: "process", key: "process" },
    { id: "services", key: "services" },
    { id: "stories", key: "stories" },
    { id: "quote", key: "quote" },
    { id: "faq", key: "faq" },
  ];

  return (
    <footer id="contact" className="bg-obsidian pt-20 text-paper">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <div className="grid grid-cols-1 gap-12 border-b border-paper/15 pb-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Logo light />
              <p className="mt-6 max-w-md text-sm leading-relaxed text-paper/60 sm:text-base">{t.footer.tagline}</p>
              <button
                data-testid="footer-cta-button"
                onClick={() => scrollToId("quote")}
                className="group mt-8 flex items-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-bold text-obsidian transition-colors duration-300 hover:bg-paper"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>

            <div className="lg:col-span-3">
              <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-paper/40">{t.footer.navTitle}</p>
              <ul className="mt-5 space-y-3">
                {links.map((l) => (
                  <li key={l.id}>
                    <button
                      data-testid={`footer-link-${l.id}`}
                      onClick={() => scrollToId(l.id)}
                      className="text-sm font-semibold text-paper/70 transition-colors duration-300 hover:text-brand"
                    >
                      {t.nav[l.key]}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-paper/40">{t.footer.contactTitle}</p>
              <ul className="mt-5 space-y-4">
                <li>
                  <a
                    data-testid="footer-email-link"
                    href={`mailto:${CONTACT.email}`}
                    className="flex items-center gap-3 text-sm font-semibold text-paper/70 transition-colors duration-300 hover:text-brand"
                  >
                    <Mail className="h-4 w-4 shrink-0 text-brand" />
                    {CONTACT.email}
                  </a>
                </li>
                <li>
                  <a
                    data-testid="footer-whatsapp-link"
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm font-semibold text-paper/70 transition-colors duration-300 hover:text-brand"
                  >
                    <MessageCircle className="h-4 w-4 shrink-0 text-brand" />
                    WhatsApp
                  </a>
                </li>
                <li className="flex items-center gap-3 text-sm font-semibold text-paper/70">
                  <MapPin className="h-4 w-4 shrink-0 text-brand" />
                  {CONTACT.location}
                </li>
              </ul>
            </div>
          </div>
        </Reveal>

        <div className="font-display font-expanded overflow-hidden py-8">
          <p className="text-stroke-light select-none whitespace-nowrap text-[18vw] font-black uppercase leading-none opacity-40 lg:text-[13rem]" style={{ WebkitTextStroke: "2px rgba(248,250,252,0.6)" }}>
            ON-ALL
          </p>
        </div>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-paper/15 py-7 sm:flex-row sm:items-center">
          <p className="font-mono text-[11px] tracking-[0.15em] text-paper/40">
            © {year} ON-ALL LLC — {t.footer.rights}
          </p>
          <div className="flex items-center gap-5">
            <p className="font-mono text-[11px] tracking-[0.15em] text-paper/40">{t.footer.excluded}</p>
            <a
              href="/admin"
              data-testid="footer-admin-link"
              className="font-mono text-[11px] tracking-[0.15em] text-paper/40 transition-colors duration-300 hover:text-brand"
            >
              Talep Paneli
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
